import React from "react";
import { useNavigate } from "react-router";
import { Helmet } from "react-helmet-async";
import { CheckCircle, LogOut, Building2, User, Wrench } from "lucide-react";

const SuccessPage = () => {
  const navigate = useNavigate();
  const firstName = localStorage.getItem("compass_first_name") || "User";
  const lastName = localStorage.getItem("compass_last_name") || "";
  const userType = localStorage.getItem("compass_user_type") || "Member";

  const typeConfig = {
    Lawyer: {
      icon: <Building2 className="w-6 h-6" />,
      label: "Firm Member",
      color: "#001C60",
      bg: "bg-blue-50",
      border: "border-blue-200",
    },
    Client: {
      icon: <User className="w-6 h-6" />,
      label: "Client",
      color: "#0d6b2a",
      bg: "bg-green-50",
      border: "border-green-200",
    },
    DIY: {
      icon: <Wrench className="w-6 h-6" />,
      label: "DIY User",
      color: "#7c3aed",
      bg: "bg-purple-50",
      border: "border-purple-200",
    },
  };

  const cfg = typeConfig[userType] || typeConfig["Client"];

  const handleLogout = () => {
    localStorage.removeItem("compass_token");
    localStorage.removeItem("compass_user_type");
    localStorage.removeItem("compass_first_name");
    localStorage.removeItem("compass_last_name");
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="success-page">
      <Helmet>
        <title>Welcome | Compass Family Law Portal</title>
        <meta
          name="description"
          content="You're signed in to the Compass Family Law Portal. Access your account, wizards, and case information."
        />
        {/* Post-auth landing — don't index. */}
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="w-full max-w-md text-center space-y-6">
        {/* Logo */}
        <div>
          <img src="/compass-logo.png" alt="Compass" width={64} height={64} className="mx-auto mb-2" style={{ width: 64, height: 64, objectFit: "contain" }} />
          <h1 className="text-2xl font-bold" style={{ color: "#001C60" }}>
            Compass Family Law Portal
          </h1>
        </div>

        {/* Success card */}
        <div className="rounded-xl border bg-white shadow-2xl p-8 space-y-5">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-9 h-9 text-green-500" />
            </div>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Login Successful!</h2>
            <p className="text-gray-500 mt-1">
              Welcome, {firstName} {lastName}
            </p>
          </div>

          {/* User type badge */}
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${cfg.bg} ${cfg.border}`}
            style={{ color: cfg.color }}
            data-testid="user-type-badge"
          >
            {cfg.icon}
            <span className="font-medium text-sm">{cfg.label} Portal</span>
          </div>

          {/* Placeholder portal notice */}
          <div className="bg-gray-50 rounded-lg p-4 text-left border border-gray-200">
            <p className="text-sm font-medium text-gray-700">Portal Coming Soon</p>
            <p className="text-xs text-gray-500 mt-1">
              Your {cfg.label.toLowerCase()} portal is under development. Check back soon for
              case management, documents, billing, and more.
            </p>
          </div>

          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
